import React, { Fragment, MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Socket } from 'socket.io-client';
import moment from 'moment';

import { ButtonBase, Divider, Grid, Popover } from '@material-ui/core';
import {
  Launch as LaunchIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
} from '@material-ui/icons';

import Wrapper from 'components/Wrapper';

import routesPath from 'config/routesPath';
import { useSocketContext } from 'socketContext';
import { TODAY_END_DAY, THIRTY_DAYS_AGO } from 'helpers/predefinedDates';

import { Creators as proposalCreators } from 'modules/resale/store/ducks/proposal';
import { NotificationContent } from 'modules/notifications/services/dto';
import {
  useGetNotifications,
  useReadNotification,
} from 'modules/notifications/services/queries';

import * as S from './styles';

const SOCKET_MAIN_EVENT = 'notification';
const SOCKET_TRIGGER_GET_NOTIFICATIONS = 'notification:getAll';

export function Notifications() {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null
  );

  const history = useHistory();
  const dispatch = useDispatch();
  const { socket }: any = useSocketContext();
  const { userSite } = useSelector(({ userStore }) => userStore);

  const popoverIsOpen = Boolean(popoverAnchor);

  const currentSocket = socket as Socket;
  const currentUserId = userSite?.id;

  const {
    data: allNotifications,
    refetch: getNotifications,
  } = useGetNotifications({
    startDate: moment(THIRTY_DAYS_AGO).valueOf(),
    endDate: moment(TODAY_END_DAY).valueOf(),
    page: 0,
    size: 100,
    userId: currentUserId,
  });

  const { mutate: readNotification } = useReadNotification();

  const hasNotifications = allNotifications?.length > 0;

  const unreadNotificationsCount =
    allNotifications?.filter(el => !el.read).length ?? 0;

  const handleOpenPopover = ({
    currentTarget,
  }: MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchor(currentTarget);
  };

  const handleClosePopover = () => setPopoverAnchor(null);

  const handleClickNotification = (notification: NotificationContent) => {
    if (notification?.id) {
      readNotification({ notificationId: notification?.id });
    }

    const evaluationsPath = routesPath?.getRecursivePath(
      routesPath?.evaluations
    );

    if (notification.type === 'NEW_EVALUATION') {
      return history.push(`${evaluationsPath}?tab=NEW`);
    }
    if (notification.type === 'EVALUATION_CONCLUDED') {
      return history.push(`${evaluationsPath}?tab=PENDING`);
    }
    if (notification.type === 'EVALUATION_FINISHED') {
      return history.push(`${evaluationsPath}?tab=FINISHED`);
    }

    let currentProposalSection: string | null = null;

    if (notification.type === 'LEAD_REMINDER') {
      currentProposalSection = 'scheduling';
    }

    return dispatch(
      proposalCreators.setProposalModalOpen(true, {
        dealerId: notification?.dealerId,
        proposalId: notification?.proposalId,
        ...(currentProposalSection && { currentProposalSection }),
      })
    );
  };

  useEffect(() => {
    if (!currentSocket) return;

    currentSocket.on(SOCKET_MAIN_EVENT, ({ type }) => {
      if (type === SOCKET_TRIGGER_GET_NOTIFICATIONS) {
        getNotifications();
      }
    });

    return () => currentSocket.off(SOCKET_MAIN_EVENT);
  }, []);

  return (
    <S.ContainerBell>
      {!unreadNotificationsCount ? (
        <ButtonBase onClick={handleOpenPopover}>
          <Wrapper flex justify="center" color="#FFF">
            <NotificationsIcon color="inherit" />
          </Wrapper>
        </ButtonBase>
      ) : (
        <ButtonBase onClick={handleOpenPopover}>
          <Wrapper flex justify="center" color="#FFF">
            <NotificationsActiveIcon />
          </Wrapper>
          <S.CircleNotificationPending
            height={'18px'}
            width={'18px'}
            ml={'13px'}
            borderRadius={'50%'}
            style={{ right: 0, top: 0 }}
          >
            <Wrapper fontSize={10}>
              {unreadNotificationsCount >= 100
                ? '+99'
                : unreadNotificationsCount}
            </Wrapper>
          </S.CircleNotificationPending>
        </ButtonBase>
      )}
      <Popover
        anchorEl={popoverAnchor}
        open={popoverIsOpen}
        onClose={handleClosePopover}
        onBackdropClick={handleClosePopover}
        style={{ marginTop: '17px' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <S.ContainerNotifications>
          <Grid container>
            <Grid item xs={6}>
              <Wrapper color="#424242" fontSize={18} fontWeight={600} mb={16}>
                Notificações
              </Wrapper>
            </Grid>
            {!hasNotifications && (
              <Wrapper
                height={120}
                width={'100%'}
                flex
                align="center"
                justify="center"
                color="#7f7d7d"
              >
                Você não tem notificações no momento.
              </Wrapper>
            )}
            {hasNotifications && (
              <Fragment>
                {allNotifications?.map(el => (
                  <Grid container key={el?.id}>
                    <Grid item xs={10}>
                      <Wrapper flex direction="row">
                        {!el.read && (
                          <S.CircleNotificationPending
                            height={'8px'}
                            width={'8px'}
                            mt={'16px'}
                            ml={'-8px'}
                            mr={'8px'}
                            borderRadius={'8px'}
                          />
                        )}
                        <Wrapper
                          flex
                          color="#424242"
                          fontSize={14}
                          fontWeight={400}
                          mt={16}
                          ml={10}
                        >
                          {el?.message}
                        </Wrapper>
                      </Wrapper>
                      <Wrapper mb={16} ml={10} color="#757575">
                        {moment(el.updatedAt).format('lll')}
                      </Wrapper>
                    </Grid>
                    <Grid item xs={2}>
                      <S.ContainerDoneAllIcon>
                        <LaunchIcon
                          fontSize="small"
                          onClick={() => handleClickNotification(el)}
                        />
                      </S.ContainerDoneAllIcon>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  </Grid>
                ))}
              </Fragment>
            )}
          </Grid>
        </S.ContainerNotifications>
      </Popover>
    </S.ContainerBell>
  );
}
